import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { VRButton } from "three/examples/jsm/webxr/VRButton.js";
import { XRControllerModelFactory } from "three/examples/jsm/webxr/XRControllerModelFactory.js";
import Stats from "stats.js";
import ArrowField from "./three.utils/ArrowField.ts";
import MagnetGeometry from "./MagnetGeometry";
import PromisifyWorker from "./PromisifyWorker";
import TetgenGeometry from "./three.utils/TetgenGeometry";

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x000000);

const magnetGeometry = new MagnetGeometry(0.1, 0.2, 0.05);
const magnet = new THREE.Mesh(magnetGeometry, new THREE.MeshNormalMaterial());
magnet.position.set(0.2, 0.2, 0.2);
scene.add(magnet);

const displayStats = new Stats();
displayStats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom

const simulationStats = new Stats();
simulationStats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom

const statContainer = document.createElement("div");
statContainer.style.position = "absolute";
statContainer.style.left = "0";
statContainer.style.top = "0";
for (const stats of [displayStats, simulationStats]) {
  stats.dom.style.position = "relative";
  stats.dom.style.float = "left";
  statContainer.appendChild(stats.dom);
}
document.body.appendChild(statContainer);

const maxwellWorkerUrl = "./maxwellWorker.js";
const maxwellWorker = new PromisifyWorker(new Worker(maxwellWorkerUrl));
const tetgenWorkerUrl = "./tetgenWorker.js";
const tetgenWorker = new PromisifyWorker(new Worker(tetgenWorkerUrl));

let field = null;
let center = null;
let tetrahedraCount = 0;
let geometryLoopID = 0;
let afterGeometryLoop = null;
let tetgenMesh = null;
tetgenWorker.addListener(
  "geometry",
  async ({ vertices: _vertices, tetrahedra: _tetrahedra }) => {
    const vertices = _vertices.slice();
    const tetrahedra = _tetrahedra.slice();
    const newTetrahedraCount = tetrahedra.length / 4;
    const fieldBuffer = new SharedArrayBuffer(4 * 3 * newTetrahedraCount);

    const newArrows = new ArrowField(
      newTetrahedraCount,
      0.05,
      0.3,
      0.15,
      8,
      true
    );
    const currentID = ++geometryLoopID;
    maxwellWorker.postMessage(
      "geometry",
      {
        center: newArrows.centerSAB,
        direction: newArrows.directionSAB,
        field: fieldBuffer,
        vertices: _vertices,
        tetrahedra: _tetrahedra,
      },
      [(_vertices.buffer, _tetrahedra.buffer)]
    );

    if (tetgenMesh) {
      scene.remove(tetgenMesh);
    }
    tetgenMesh = new THREE.Mesh(
      new TetgenGeometry(vertices, tetrahedra),
      new THREE.MeshBasicMaterial({
        wireframe: true,
      })
    );
    scene.add(tetgenMesh);
    await maxwellWorker.onMessage("solutionUpdated");
    if (afterGeometryLoop !== null) await afterGeometryLoop();
    let firstUpdate = true;
    tetrahedraCount = newTetrahedraCount;
    field = new Float32Array(fieldBuffer);
    center = newArrows.centerBuffer.array;

    const afterGeometry = [];
    afterGeometryLoop = () =>
      new Promise((resolve) => afterGeometry.push(resolve));
    // while (geometryLoopID == currentID) {
    await new Promise((resolve) => afterAnimation.push(resolve));
    updateRHS();
    simulationStats.begin();
    maxwellWorker.postMessage("solveSystem");
    await maxwellWorker.onMessage("solutionUpdated");
    if (firstUpdate) {
      if (arrows !== null) scene.remove(arrows);
      arrows = newArrows;
      scene.add(arrows);
      firstUpdate = false;
    }
    arrows.centerBuffer.needsUpdate = true;
    arrows.directionBuffer.needsUpdate = true;
    simulationStats.end();
    //}
    //while (afterGeometry.length > 0) afterGeometry.pop()();
  }
);

function updateGeometry(updateMagnetGeometry = false) {
  magnet.updateMatrixWorld();
  const message = {
    transform: magnet.matrixWorld.elements,
  };
  if (updateMagnetGeometry)
    message.magnet = {
      radius: magnetGeometry.radius,
      height: magnetGeometry.height,
      size: magnetGeometry.size,
    };
  tetgenWorker.postMessage("update", message);
}

window.updateGeometry = updateGeometry;

(async () => {
  await Promise.all([
    tetgenWorker.onMessage("initialized"),
    maxwellWorker.onMessage("initialized"),
  ]);
  updateGeometry(true);
})();

let arrows = null;
const afterAnimation = [];

const inverseTransformTmp = new THREE.Matrix4();
const vectorTmp = new THREE.Vector3();
function updateRHS() {
  const angle = ((2 * Math.PI) / 10) * clock.getElapsedTime();
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);
  if (renderer.xr.isPresenting) {
    for (let i = 0; i < 3 * tetrahedraCount; i += 3) {
      const x = center[i];
      const y = center[i + 1];
      const z = center[i + 2];

      field.set([0, 0, 0], i);
      let d = 1;
      for (const hand of hands) {
        if (
          Math.hypot(
            x - hand.position.x,
            y - hand.position.y,
            z - hand.position.z
          ) < 0.3
        ) {
          field.set([0, d, 0], i);
        }
        d *= -1;
      }
    }
  } else {
    magnet.updateMatrixWorld();
    inverseTransformTmp.getInverse(magnet.matrixWorld);
    for (let i = 0; i < 3 * tetrahedraCount; i += 3) {
      const { x, y, z } = vectorTmp
        .set(center[i], center[i + 1], center[i + 2])
        .applyMatrix4(inverseTransformTmp);

      if (magnetGeometry.contains([x, y, z])) {
        field.set(magnetGeometry.magneticField([x, y, z]), i);
      } else if (y < -0.5) {
        field.set([0, 0.01 * Math.sign(cosAngle * x + sinAngle * z), 0], i);
      } else {
        field.set([0, 0, 0], i);
      }
    }
  }
}

var camera = new THREE.PerspectiveCamera(
  40,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
);
camera.position.set(0.6, 1, 2.5);
camera.lookAt(0, 1, 0);

var vrCamera = new THREE.PerspectiveCamera(
  40,
  window.innerWidth / window.innerHeight,
  0.1,
  1000
);

const renderer = new THREE.WebGLRenderer({
  antialias: true,
});
if (
  renderer.capabilities.isWebGL2 === false &&
  renderer.extensions.get("ANGLE_instanced_arrays") === null
) {
  console.error("Not supported!");
}
renderer.xr.enabled = true;
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setSize(window.innerWidth, window.innerHeight);
document.body.appendChild(renderer.domElement);

function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();

  renderer.setSize(window.innerWidth, window.innerHeight);
}

window.addEventListener("resize", onWindowResize, false);

document.body.appendChild(VRButton.createButton(renderer));

const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(0, 0, 0);

const hands = [
  renderer.xr.getControllerGrip(0),
  renderer.xr.getControllerGrip(1),
];
const controllerModelFactory = new XRControllerModelFactory();
for (const hand of hands) {
  hand.add(controllerModelFactory.createControllerModel(hand));
  scene.add(hand);

  var geometry = new THREE.SphereBufferGeometry(0.01, 12, 12);
  var material = new THREE.MeshBasicMaterial({ color: 0x0000ff });
  var sphere = new THREE.Mesh(geometry, material);
  hand.add(sphere);
}

const displayTetgen = document.getElementById("settings-display-tetgen");

const clock = new THREE.Clock();
let lastGeometryUpdate = 0;
magnet.matrixAutoUpdate = false;
renderer.setAnimationLoop(() => {
  displayStats.begin();
  if (arrows != null) {
    if (renderer.xr.isPresenting) {
      arrows.arrowScale = 5;
      arrows.maxLength = 0.1;
    } else {
      arrows.arrowScale = 1;
      arrows.maxLength = 0.05;
    }
  }
  if (tetgenMesh) {
    tetgenMesh.visible = !renderer.xr.isPresenting && displayTetgen.checked;
  }

  //  magnet.matrix.makeRotationY(clock.getElapsedTime());
  magnet.matrixWorldNeedsUpdate = true;
  controls.update();
  renderer.render(scene, renderer.xr.isPresenting ? vrCamera : camera);
  displayStats.end();
  while (afterAnimation.length > 0) afterAnimation.pop()();
  if (clock.getElapsedTime() - lastGeometryUpdate > 5) {
    // updateGeometry();
    lastGeometryUpdate = clock.getElapsedTime();
  }
});
